import { getSessionUUID } from '../components/lib/AuthToken';
import logger from '../utils/logger';


/**
 * Upload a new payslip PDF and assign it to a driver.
 * Only callable by super admin / dev users.
 */
export const upload_payslip = async (file, profileId, displayName, payDate) => {
    try {
        const sessionToken = await getSessionUUID();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('profile_id', profileId);
        formData.append('display_name', displayName);
        formData.append('pay_date', payDate);

        const response = await fetch('/payslips/upload', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: formData,
        });

        const responseBody = await response.json();
        return {
            message: responseBody.message || 'Request successful',
            status: response.status,
            data: responseBody || null,
        };
    } catch (err) {
        logger.error('Error uploading payslip:', err);
        return { message: err.message || 'An error occurred', status: 500 };
    }
};


/**
 * List payslips.
 * Super admin / dev: all payslips, optionally filtered by profileId.
 * Regular user: their own payslips only.
 */
export const list_payslips = async (profileIdFilter = '', year = null, month = null) => {
    try {
        const sessionToken = await getSessionUUID();
        const params = new URLSearchParams();
        if (profileIdFilter) params.set('profile_id', profileIdFilter);
        if (year) params.set('year', year);
        if (month) params.set('month', month);
        const url = params.toString() ? `/payslips/list?${params}` : '/payslips/list';

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
            },
        });

        const responseBody = await response.json();
        return {
            message: 'Request successful',
            status: response.status,
            data: responseBody || null,
        };
    } catch (err) {
        logger.error('Error fetching payslips:', err);
        return { message: err.message || 'An error occurred', status: 500 };
    }
};


/**
 * Get distinct years (or months for a given year) for folder navigation.
 * Only callable by super admin / dev users.
 */
export const list_payslip_periods = async (year = null) => {
    try {
        const sessionToken = await getSessionUUID();
        const url = year ? `/payslips/periods?year=${year}` : '/payslips/periods';
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
            },
        });
        const responseBody = await response.json();
        return { status: response.status, data: responseBody || null };
    } catch (err) {
        logger.error('Error fetching payslip periods:', err);
        return { status: 500 };
    }
};


/**
 * Get a 1-hour signed download URL for a specific payslip.
 */
export const get_payslip_url = async (payslipId) => {
    try {
        const sessionToken = await getSessionUUID();

        const response = await fetch('/payslips/url', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ payslip_id: payslipId }),
        });

        const responseBody = await response.json();
        return {
            message: 'Request successful',
            status: response.status,
            data: responseBody || null,
        };
    } catch (err) {
        logger.error('Error getting payslip URL:', err);
        return { message: err.message || 'An error occurred', status: 500 };
    }
};


/**
 * Permanently delete a payslip.
 * Only callable by super admin / dev users.
 */
export const delete_payslip = async (payslipId) => {
    try {
        const sessionToken = await getSessionUUID();

        const response = await fetch('/payslips/delete', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ payslip_id: payslipId }),
        });

        const responseBody = await response.json();
        return {
            message: responseBody.message || 'Request successful',
            status: response.status,
            data: responseBody || null,
        };
    } catch (err) {
        logger.error('Error deleting payslip:', err);
        return { message: err.message || 'An error occurred', status: 500 };
    }
};
